// OnboardingV2.tsx — the re-sequenced first-run flow (config/features
// FIRST_SESSION_ONBOARDING_V2). ONE linear controller that replaces BOTH the old
// FirstSession reassurance run AND the post-session ConsentGate, in the order from
// the v2 design:
//   1 Welcome (+ safety exit) → 2 Your details → 3 Enter code →
//   4 Recovery, gently (merged ×4) → 5 Terms → 6 Which best describes you? (fork) →
//   7 Take part in research? (survivor only) → 8 Needs check (survivor only, last before
//   the door) → 9 Entry (branched by type).
//
// It reads/writes the SAME state flags as the old flow (onboardingDone,
// termsAcceptedVersion, userType, researchConsent) so resume + returning-user logic
// is unchanged, and the old FirstSession/ConsentGate stay fully intact behind the
// flag (reversible).
//
// PURCHASE WALL (see NATIVE-VS-WEB.md): the "free 10-day trial" badge + trial framing
// on the Welcome / details screens are WEB-ONLY (gated on SHOW_PURCHASE). Native ships
// purchase-free — neutral copy, membership managed on the website. Everything from the
// reassurance screen onward is identical on both platforms.
import React from 'react';
import { Capacitor } from '@capacitor/core';
import { Display, Body, Btn } from '@/components/ui';
import { Icon, type IconName, Wordmark } from '@/components/icons';
import { trackOnboarding } from '@/services/metrics';
import { useProgress } from '@/hooks/useProgress';
import { useAuth } from '@/hooks/useAuth';
import { useGuide } from '@/flows/guide/useGuide';
import { clipUrl } from '@/data/guideMessages';
import { AccountSignIn } from '@/components/AccountSignIn';
import { ResearchConsentStep } from './ResearchConsentStep';
import { CrisisSupport } from '@/flows/care/CrisisSupport';
import { saveUserType, isReturningAccount } from '@/services/auth';
import { logConsent } from '@/services/consent';
import { canSync, postResearchConsent, openFogsCheck } from '@/services/sync';
import { CONSENT_VERSIONS, TERMS_URL, PRIVACY_URL } from '@/config/consent';
import { openExternal } from '@/services/openExternal';
import { SHOW_PURCHASE, SHOW_IAP } from '@/config/supabase';
import { ONBOARDING_2026, FOGS_FULL_CHECK } from '@/config/features';
import { RemindersStep } from './RemindersStep';
import { NeedsCheckStep } from './NeedsCheckStep';
import { setPostOnboardingStart } from './postOnboardingStart';

type Stage = 'welcome' | 'account' | 'reassurance' | 'terms' | 'fork' | 'research' | 'reminders' | 'needs' | 'entry';
type UserType = 'survivor' | 'curious' | 'staff' | 'other';

const STAGE_INDEX: Record<Stage, number> = {
  welcome: 0, account: 1, reassurance: 2, terms: 3, fork: 4, research: 5, reminders: 6, needs: 7, entry: 8,
};

const inlineLink: React.CSSProperties = {
  background: 'none', border: 0, padding: 0, cursor: 'pointer', font: 'inherit',
  color: 'var(--amber-deep)', fontWeight: 600, textDecoration: 'underline',
};

const optStyle: React.CSSProperties = {
  textAlign: 'left', padding: '15px 17px', borderRadius: 14,
  border: '1px solid var(--hair)', background: 'var(--card-top)', color: 'var(--ink)',
  font: 'inherit', fontSize: 15, fontWeight: 500, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
};

/** The full-screen scroller shared by every step (matches FirstSession/ConsentGate). */
function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ position: 'relative', width: '100%', height: '100dvh', maxWidth: 520, margin: '0 auto', background: 'var(--cream)', overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
      <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', padding: 'calc(26px + var(--safe-top)) 22px calc(26px + var(--safe-bottom))' }}>
        {children}
      </div>
    </div>
  );
}

/** A soft circular icon badge — the bit of warmth that replaces a wall of text. */
function Badge({ name, size = 66 }: { name: IconName; size?: number }) {
  return (
    <div aria-hidden="true" style={{ width: size, height: size, borderRadius: 999, background: 'rgba(200,134,26,0.13)', color: 'var(--amber-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon name={name} size={Math.round(size * 0.45)} />
    </div>
  );
}

/** Calm 3-dot run for the "settling in" steps (reassurance → terms → fork → research). */
function StepDots({ step }: { step: 1 | 2 | 3 }) {
  return (
    <div style={{ display: 'flex', gap: 7, margin: '14px 0 4px' }}>
      {[1, 2, 3].map((n) => (
        <span key={n} style={{ width: n === step ? 22 : 8, height: 8, borderRadius: 999, background: n < step ? 'var(--green)' : n === step ? 'var(--amber-deep)' : 'var(--hair-strong)', transition: 'width .25s, background .25s' }} />
      ))}
    </div>
  );
}

function BackBtn({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} aria-label="Back" style={{ width: 40, height: 40, borderRadius: 999, border: '1px solid var(--hair)', background: 'var(--card-top)', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
      <Icon name="chevLeft" size={18} />
    </button>
  );
}

export function OnboardingV2() {
  const progress = useProgress();
  const auth = useAuth();
  const guide = useGuide();

  const [stage, setStage] = React.useState<Stage>('welcome');
  // Anonymous funnel telemetry: count each onboarding stage reached (drop-off),
  // plus a 'finished' event below. Aggregate only, never blocks.
  React.useEffect(() => { trackOnboarding(stage); }, [stage]);
  const [mode, setMode] = React.useState<'create' | 'signin'>('create');
  const [saving, setSaving] = React.useState(false);
  const [crisisOpen, setCrisisOpen] = React.useState(false);
  // Did we walk the user through any consent step this run? Drives the entry copy: a
  // returning sign-in whose profile already holds everything gets "Welcome back".
  const walkedConsent = React.useRef(false);

  const profileUserType = (auth.session?.user?.userType ?? null) as UserType | null;

  // Welcome image (guide H1) — picked + recorded once, like the old FirstSession.
  const [h1] = React.useState(() => guide.pick('H1_welcome'));
  React.useEffect(() => {
    if (h1) guide.record(h1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    progress.recordOnboardingStep(STAGE_INDEX[stage]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // Where the run ends. ONBOARDING_2026 adds one last, optional step before the door:
  // the invitation to be reminded to come back. It is offered only where a reminder
  // can actually fire (native local notifications; on web nothing is scheduled and it
  // would be a lie), and only to someone who has none set, so a returning user who
  // already has one is never asked again.
  //
  // After that comes the survivor-only needs check. Only someone who walked the consent
  // steps this run sees it (walkedConsent), so a returning sign-in whose profile already
  // holds everything goes straight to the door, as before.
  const afterReminders = React.useCallback((): Stage => {
    const ut = progress.state.userType ?? profileUserType;
    return ut === 'survivor' && walkedConsent.current ? 'needs' : 'entry';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress.state.userType, profileUserType]);

  const endStage = React.useCallback((): Stage => {
    if (!ONBOARDING_2026) return 'entry';
    if (!Capacitor.isNativePlatform()) return afterReminders();
    if (progress.state.morningAnchorTime) return afterReminders();
    return 'reminders';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress.state.morningAnchorTime, afterReminders]);

  // The next consent step that still needs doing, in order. Returns the end stage when
  // nothing remains (e.g. a returning user whose profile already has it all).
  const nextConsentStage = React.useCallback((): Stage => {
    if (progress.state.termsAcceptedVersion !== CONSENT_VERSIONS.terms) return 'terms';
    const ut = progress.state.userType ?? profileUserType;
    if (ut == null) return 'fork';
    if (ut === 'survivor' && progress.state.researchConsent === 'unset') return 'research';
    return endStage();
  }, [progress.state.termsAcceptedVersion, progress.state.userType, progress.state.researchConsent, profileUserType, endStage]);

  // A session appearing during the 'account' step moves us on: new accounts get the
  // warm reassurance screen; a returning sign-in skips it to the first pending consent
  // step (often none → straight to a "welcome back" entry).
  //
  // We wait for `signingIn` to clear, not just for the session. confirmOtp sets the
  // session the moment the code verifies, then awaits the org-code/domain resolution
  // before setting `welcome` — advancing on the session alone painted the next step
  // during that network gap, and the WelcomeScreen overlay then dropped on top of it
  // (the "flash"). Clearing signingIn is batched with setWelcome, so by the time we
  // advance the overlay is already covering the change.
  //
  // An email that ALREADY has an account never gets the new-account route, however they
  // arrived here: "Create my account" with a registered address silently becomes a
  // returning sign-in, so they skip the first-run reassurance and pick up at whatever
  // consent (if any) is still outstanding. See isReturningAccount for why this is
  // decided after the code verifies rather than at the email field.
  React.useEffect(() => {
    if (stage !== 'account' || !auth.session || auth.signingIn) return;
    if (mode === 'create' && !isReturningAccount(auth.session)) { setStage('reassurance'); return; }
    if (mode === 'create') setMode('signin');
    setStage(nextConsentStage());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, auth.session, auth.signingIn]);

  // App killed mid-flow and reopened with a session already on hand: skip the
  // sign-in screens and resume at the first outstanding consent step.
  React.useEffect(() => {
    if (stage === 'welcome' && auth.session) setStage(nextConsentStage());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.session]);

  const finishOnboarding = () => { trackOnboarding('finished'); progress.completeOnboarding(); };

  // --- actions ---------------------------------------------------------------
  const startAccount = (m: 'create' | 'signin') => {
    setMode(m);
    // No backend (dev / ungated): nothing to sign into — go straight to the warm
    // reassurance screen, then enter. Consent gating only applies with auth on
    // (mirrors the old flow, where ConsentGate required auth.enabled + a session).
    if (!auth.enabled) { setStage('reassurance'); return; }
    setStage('account');
  };

  const afterReassurance = () => {
    if (!auth.enabled) { setStage(endStage()); return; }
    setStage(nextConsentStage());
  };

  const agreeTerms = () => {
    progress.acceptTerms(CONSENT_VERSIONS.terms);
    void logConsent('terms', 'accepted', CONSENT_VERSIONS.terms);
    walkedConsent.current = true;
    const ut = progress.state.userType ?? profileUserType;
    if (ut == null) { setStage('fork'); return; }
    if (ut === 'survivor' && progress.state.researchConsent === 'unset') { setStage('research'); return; }
    setStage(endStage());
  };

  // Who is this? Chosen once, written to the profile (which gates the research PID).
  // Only a survivor continues to research; everyone else gets the full app but skips
  // research entirely (auto-declined so the record is explicit).
  const chooseType = (type: UserType) => {
    if (saving) return;
    walkedConsent.current = true;
    progress.setUserType(type);
    void saveUserType(type);
    if (type === 'survivor') { setStage('research'); return; }
    progress.setResearchConsent('declined', CONSENT_VERSIONS.research);
    void logConsent('research', 'declined', CONSENT_VERSIONS.research);
    setStage(endStage());
  };

  const decideResearch = (granted: boolean) => {
    if (saving) return;
    setSaving(true);
    const decision: 'granted' | 'declined' = granted ? 'granted' : 'declined';
    progress.setResearchConsent(decision, CONSENT_VERSIONS.research);
    void logConsent('research', decision, CONSENT_VERSIONS.research);
    // Mirror the decision to the research backend so its record matches (best-effort).
    const s = auth.session;
    if (canSync(s) && s.accessToken) void postResearchConsent(s.accessToken, granted);
    setSaving(false);
    setStage(endStage());
  };

  // --- screens ---------------------------------------------------------------
  let body: React.ReactNode = null;

  if (stage === 'welcome') {
    body = (
      // ONBOARDING_2026: this screen is what the intro run hands over to, and it lands
      // on the same cream paper the run was drawn on. The run fades itself out first,
      // so this arrives INTO the space it left rather than replacing it — hence the
      // entrance here, and the extra beat before the buttons.
      <div
        style={{
          flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center',
          ...(ONBOARDING_2026 ? { animation: 'fadeUp 760ms cubic-bezier(0.22,0.61,0.36,1) both' } : null),
        }}
      >
        {/* ONBOARDING_2026 shows the badge on native too. It was hidden there only
            because it was tied to SHOW_PURCHASE, which is a PURCHASE-wall flag, not a
            trial one: naming a free trial sells nothing and links nowhere. */}
        {(SHOW_PURCHASE || ONBOARDING_2026) && (
          <span style={{ alignSelf: 'center', display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid rgba(200,134,26,0.30)', background: 'rgba(200,134,26,0.10)', color: 'var(--amber-deep)', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '5px 12px', borderRadius: 999, marginBottom: 16 }}>
            <Icon name="leaf" size={13} /> Free 10-day trial
          </span>
        )}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}><Wordmark h={40} /></div>
        <Display parts={{ pre: 'Welcome to ', em: 'ReNurture', post: '.' }} size={28} lh={1.16} style={{ textAlign: 'center' }} />
        {/* ONBOARDING_2026 drops the "a calm, clinician-built space" line and opens
            with the offer instead. Someone deciding whether to hand over an email
            wants to know what they get, and the two answers that matter are: the
            trial unlocks everything, and one course is theirs permanently. The old
            opener said neither, and it pushed both down the page. */}
        {ONBOARDING_2026 ? (
          <>
            <Body size={15.5} style={{ margin: '16px auto 0', maxWidth: 330, lineHeight: 1.65, textAlign: 'center' }}>
              Your <strong style={{ color: 'var(--ink)', fontWeight: 700 }}>free 10-day trial</strong> unlocks
              everything: all the tools, the meditations, and the opening chapters of the programme.
            </Body>
            <Body size={15.5} style={{ margin: '14px auto 0', maxWidth: 330, lineHeight: 1.65, textAlign: 'center' }}>
              And the{' '}
              <strong style={{ color: 'var(--ink)', fontWeight: 700 }}>self-compassion course is yours to keep, free, forever</strong>,
              even after the trial ends.
            </Body>
          </>
        ) : (
          <>
            <Body size={15} style={{ margin: '16px auto 0', maxWidth: 330, lineHeight: 1.7, textAlign: 'center' }}>
              A calm, clinician-built space to recover from a difficult relationship — at your own pace, in your own time.
            </Body>
            {SHOW_PURCHASE && (
              <Body size={14} style={{ margin: '12px auto 0', maxWidth: 330, lineHeight: 1.65, textAlign: 'center', color: 'var(--ink-soft)' }}>
                Your trial unlocks everything. The self-compassion course is{' '}
                <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>yours to keep, free</strong>, even after it ends.
              </Body>
            )}
          </>
        )}
        {/* "free" is the answer to the question a first-time visitor is actually
            asking, so the native build names it too (ONBOARDING_2026) even though it
            carries no purchasing. A free account is free on every platform. */}
        <Btn
          full
          size="lg"
          onClick={() => startAccount('create')}
          style={{ marginTop: 26, ...(ONBOARDING_2026 ? { animation: 'fadeUp 760ms cubic-bezier(0.22,0.61,0.36,1) both', animationDelay: '260ms' } : null) }}
        >
          {SHOW_PURCHASE || ONBOARDING_2026 ? 'Create my free account' : 'Create my account'}
        </Btn>
        <Btn
          full
          variant="ghost"
          size="lg"
          onClick={() => startAccount('signin')}
          style={{ marginTop: 4, ...(ONBOARDING_2026 ? { animation: 'fadeUp 760ms cubic-bezier(0.22,0.61,0.36,1) both', animationDelay: '360ms' } : null) }}
        >
          I already have an account
        </Btn>
        <button type="button" onClick={() => setCrisisOpen(true)} style={{ background: 'none', border: 0, cursor: 'pointer', font: 'inherit', fontSize: 12.5, color: 'var(--ink-soft)', textDecoration: 'underline', textUnderlineOffset: 2, marginTop: 18, padding: 8 }}>
          In danger right now? Get urgent help
        </button>
      </div>
    );
  } else if (stage === 'account') {
    body = (
      <>
        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <BackBtn onClick={() => setStage('welcome')} />
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}><Wordmark h={30} /></div>
          <Display
            parts={mode === 'create'
              ? { pre: 'Create your ', em: SHOW_PURCHASE || ONBOARDING_2026 ? 'free account' : 'account', post: '.' }
              : { pre: 'Sign in to ', em: 'continue', post: '.' }}
            size={26}
            lh={1.18}
            style={{ textAlign: 'center' }}
          />
          {/* ONBOARDING_2026: the trial was just made on the previous screen, so it is
              not restated here. What IS worth carrying across the ask for an email is
              the one thing this free account gives permanently. */}
          {ONBOARDING_2026 && mode === 'create' ? (
            <>
              <Body size={14.5} style={{ margin: '14px auto 0', maxWidth: 330, lineHeight: 1.7, textAlign: 'center' }}>
                Passwordless and private: we’ll email you a sign-in code.
              </Body>
              <Body size={14.5} style={{ margin: '10px auto 6px', maxWidth: 330, lineHeight: 1.7, textAlign: 'center' }}>
                With this free account the{' '}
                <strong style={{ color: 'var(--ink)', fontWeight: 700 }}>self-compassion course is yours permanently</strong>, free.
              </Body>
            </>
          ) : (
            <Body size={14.5} style={{ margin: '14px auto 6px', maxWidth: 330, lineHeight: 1.7, textAlign: 'center' }}>
              {mode === 'create'
                ? (SHOW_PURCHASE
                  ? 'Your free 10-day trial unlocks every feature, and the self-compassion course stays yours to keep, free, afterwards.'
                  : SHOW_IAP
                    ? 'Passwordless and private. We’ll email you a sign-in code.'
                    : 'Passwordless and private — we’ll email you a sign-in code. Membership is managed on the ReNurture website.')
                : 'Welcome back. Enter your email and we’ll send you a sign-in code.'}
            </Body>
          )}
          <AccountSignIn mode={mode} />
        </div>
      </>
    );
  } else if (stage === 'reassurance') {
    body = (
      <>
        {!ONBOARDING_2026 && <div style={{ alignSelf: 'flex-start' }}><Wordmark h={20} /></div>}
        <StepDots step={1} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center' }}>
          <img src={clipUrl('welcome')} alt="" aria-hidden="true" style={{ display: 'block', width: 'auto', maxWidth: '62%', maxHeight: 180, margin: '0 auto 12px', objectFit: 'contain' }} />
          <Display parts={{ pre: 'Recovery, ', em: 'gently', post: '.' }} size={27} lh={1.18} style={{ textAlign: 'center' }} />
          <Body size={15} style={{ margin: '16px auto 0', maxWidth: 330, lineHeight: 1.8, textAlign: 'center' }}>
            I’m your guide — a psychologist — and I’ll be alongside you. The programme is small steps, taken whenever you have the space. A few minutes is plenty.
          </Body>
          <Body size={15} style={{ margin: '14px auto 0', maxWidth: 330, lineHeight: 1.8, textAlign: 'center' }}>
            Come and go as you need. Nothing is lost when you step away, and there’s no finish line to fall behind.
          </Body>
          <Btn full size="lg" onClick={afterReassurance} style={{ marginTop: 26 }}>Get started</Btn>
        </div>
      </>
    );
  } else if (stage === 'terms') {
    body = (
      <>
        {!ONBOARDING_2026 && <div style={{ alignSelf: 'flex-start' }}><Wordmark h={20} /></div>}
        <StepDots step={1} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', padding: '14px 0 4px' }}>
          <Badge name="heart" />
          <Display text="We’re really glad you’re here." size={27} weight={400} lh={1.16} style={{ margin: '16px 0 12px' }} />
          <Body size={15.5} style={{ lineHeight: 1.6, color: 'var(--ink-soft)', marginBottom: 18, maxWidth: '32ch' }}>
            A calm, private space to heal at your own pace. Before you start, a few things to understand and agree to.
          </Body>
          <div style={{ background: 'var(--card-top)', border: '1px solid var(--hair)', borderRadius: 16, padding: '2px 16px' }}>
            {([
              ['Educational, not clinical care.', 'ReNurture is a self-guided recovery programme, not a substitute for therapy, counselling or medical care.'],
              ['Your privacy comes first.', 'Anything you write stays on your own device, and you can leave at any moment.'],
              ['Not a crisis service.', 'If you are in immediate danger, please call 999.'],
            ] as const).map(([t, b], i, arr) => (
              <div key={i} style={{ padding: '13px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--hair)' : 'none' }}>
                <Body size={13.5} style={{ lineHeight: 1.5 }}>
                  <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>{t}</strong> {b}
                </Body>
              </div>
            ))}
          </div>
        </div>
        <div style={{ marginTop: 18 }}>
          <Btn full size="lg" onClick={agreeTerms}>I understand and agree</Btn>
          <Body size={12.5} style={{ textAlign: 'center', color: 'var(--ink-soft)', marginTop: 11, lineHeight: 1.5 }}>
            You also agree to our{' '}
            <button type="button" style={inlineLink} onClick={() => openExternal(TERMS_URL)}>Terms</button>{' '}and{' '}
            <button type="button" style={inlineLink} onClick={() => openExternal(PRIVACY_URL)}>Privacy Policy</button>.
          </Body>
        </div>
      </>
    );
  } else if (stage === 'fork') {
    const OPTIONS: { type: UserType; label: string }[] = [
      { type: 'survivor', label: 'I’m here for my own recovery' },
      { type: 'curious', label: 'I’m exploring out of interest' },
      { type: 'staff', label: 'I’m a professional or staff member looking around' },
      { type: 'other', label: 'Something else' },
    ];
    body = (
      <>
        {!ONBOARDING_2026 && <div style={{ alignSelf: 'flex-start' }}><Wordmark h={20} /></div>}
        <StepDots step={2} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', padding: '14px 0 4px' }}>
          <Badge name="you" />
          <Display text="Which best describes you?" size={27} weight={400} lh={1.16} style={{ margin: '16px 0 12px' }} />
          <Body size={15.5} style={{ lineHeight: 1.6, color: 'var(--ink-soft)', marginBottom: 18, maxWidth: '34ch' }}>
            {ONBOARDING_2026
              ? 'This shapes your experience, and decides what, if anything, we ever collect. Please choose what fits best; this one can’t be changed later.'
              : 'This shapes your experience — and decides what, if anything, we ever collect. Please choose what fits best; this one can’t be changed later.'}
          </Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {OPTIONS.map((o) => (
              <button key={o.type} type="button" disabled={saving} onClick={() => chooseType(o.type)} style={optStyle}>
                {o.label}
                <Icon name="chevRight" size={16} />
              </button>
            ))}
          </div>
        </div>
      </>
    );
  } else if (stage === 'research') {
    body = (
      <>
        {!ONBOARDING_2026 && <div style={{ alignSelf: 'flex-start' }}><Wordmark h={20} /></div>}
        <StepDots step={3} />
        <ResearchConsentStep onDecide={decideResearch} saving={saving} />
      </>
    );
  } else if (stage === 'reminders') {
    body = (
      <RemindersStep
        onDone={() => setStage(afterReminders())}
        onSet={(time) => { progress.setMorningAnchor(time); setStage(afterReminders()); }}
      />
    );
  } else if (stage === 'needs') {
    // Funnel counts only (no answers, nothing stored): stage view is tracked above.
    body = (
      <NeedsCheckStep
        firstName={auth.session?.user?.firstName ?? undefined}
        // The reveal is the door for a survivor: no separate 'entry' screen. The choice
        // (never the answers) is handed to App, which lands them accordingly.
        onStart={(choice) => {
          trackOnboarding('needs-done');
          trackOnboarding(`start-${choice}`);
          setPostOnboardingStart(choice);
          finishOnboarding();
        }}
        onSkip={() => { trackOnboarding('needs-skipped'); setStage('entry'); }}
        onThemes={(themes) => progress.setNeedsThemes(themes)}
        onFullCheck={FOGS_FULL_CHECK ? () => {
          void Promise.resolve(openFogsCheck('onboarding', {
            token: canSync(auth.session) ? auth.session.accessToken : null,
            consent: progress.state.researchConsent === 'granted',
          })).then((mode) => { if (mode !== 'failed') progress.markFogsOpened(mode); }).catch(() => {});
        } : undefined}
      />
    );
  } else {
    // entry — branched by user type (and "welcome back" for a returning sign-in).
    const ut = progress.state.userType ?? profileUserType;
    const variant: 'welcomeback' | 'recovery' | 'explore' =
      mode === 'signin' && !walkedConsent.current ? 'welcomeback' : ut === 'survivor' ? 'recovery' : 'explore';
    body = (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center' }}>
        {variant === 'recovery' && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}><Wordmark h={40} /></div>
        )}
        {variant === 'explore' && (<div style={{ alignSelf: 'center', marginBottom: 16 }}><Badge name="search" /></div>)}
        {variant === 'welcomeback' && (<div style={{ alignSelf: 'center', marginBottom: 16 }}><Badge name="heart" /></div>)}
        {variant === 'recovery' && (
          <>
            <Display parts={{ pre: 'You’re ', em: 'all set', post: '.' }} size={27} lh={1.18} style={{ textAlign: 'center' }} />
            <Body size={15} style={{ margin: '16px auto 0', maxWidth: 330, lineHeight: 1.75, textAlign: 'center' }}>
              Take a breath. There’s no rush, and nothing you need to do today. We’ll begin wherever feels right for you.
            </Body>
          </>
        )}
        {variant === 'explore' && (
          <>
            <Display parts={{ pre: 'You’re set to ', em: 'explore', post: '.' }} size={27} lh={1.18} style={{ textAlign: 'center' }} />
            <Body size={15} style={{ margin: '16px auto 0', maxWidth: 330, lineHeight: 1.75, textAlign: 'center' }}>
              You’ve chosen to look around without saving personal data. Nothing you do here is collected or stored to you — change your mind any time in Settings.
            </Body>
          </>
        )}
        {variant === 'welcomeback' && (
          <>
            <Display parts={{ pre: 'Welcome to your ', em: 'membership', post: '.' }} size={26} lh={1.18} style={{ textAlign: 'center' }} />
            <Body size={15} style={{ margin: '16px auto 0', maxWidth: 330, lineHeight: 1.75, textAlign: 'center' }}>
              Everything’s unlocked. There’s no rush — move through the programme at the pace that feels right for you.
            </Body>
          </>
        )}
        <Btn full size="lg" onClick={finishOnboarding} style={{ marginTop: 26 }}>
          {variant === 'welcomeback' ? 'Enter' : 'Enter ReNurture'}
        </Btn>
      </div>
    );
  }

  return (
    <>
      <Frame>{body}</Frame>
      {crisisOpen && <CrisisSupport onClose={() => setCrisisOpen(false)} />}
    </>
  );
}
